import { FormEvent } from 'react';
import { useTranslation } from '@/i18n';
import { MIN_WITHDRAW_AMOUNT } from './withdrawAccounts';

type Props = {
  balance: number;
  amount: string;
  onAmountChange: (value: string) => void;
  onNext: () => void;
};

/** Step 1: show the available balance and take the number of coins to withdraw. */
const WithdrawAmountStep = ({ balance, amount, onAmountChange, onNext }: Props) => {
  const { t } = useTranslation();
  const value = Number(amount);
  const isBelowMinimum = value < MIN_WITHDRAW_AMOUNT;
  const isOverBalance = value > balance;
  const isInvalid = isBelowMinimum || isOverBalance;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isInvalid) onNext();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-xl border border-gold/30 bg-gold/10 px-4 py-5 text-center">
        <p className="text-sm text-shell-muted">{t('deposit.availableBalance')}</p>
        <p className="mt-1 text-2xl font-bold text-gold">
          {balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>

      <div>
        <label htmlFor="withdraw-coins" className="mb-2 block text-base font-medium">
          {t('withdraw.coins')}
        </label>
        <input
          id="withdraw-coins"
          value={amount}
          onChange={(event) => onAmountChange(event.target.value.replace(/\D/g, '').slice(0, 9))}
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          placeholder={t('deposit.enterCoins')}
          className="h-12 w-full rounded-xl border border-shell-border bg-shell-raised px-4 text-base text-shell-fg outline-none transition placeholder:text-shell-muted focus:border-gold"
        />
        <p
          className={`mt-2 text-sm ${amount && isInvalid ? 'text-danger-red' : 'text-shell-muted'}`}
        >
          {amount && isOverBalance
            ? t('withdraw.overBalance')
            : t('withdraw.minimum', { min: MIN_WITHDRAW_AMOUNT })}
        </p>
      </div>

      <button
        type="submit"
        disabled={isInvalid}
        className="h-12 w-full rounded-xl bg-gold text-base font-semibold text-gold-foreground transition hover:bg-gold-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t('withdraw.continue')}
      </button>
    </form>
  );
};

export default WithdrawAmountStep;
